import React from 'react';
import moment from 'moment';
import Icon from '../../ui/icon/icon';
import Button from '../../ui/button/button';
import { parsedHTML } from '../../../resale/buyer/components/utils';
import formattedAmount from '../../../resale/seller/components/utils';
import Counter from '../../ui/counter/counter';
import { useTranslation } from 'react-i18next';
import './rateCard.css';

export function RateCounter({
    rate,
    setQuantity,
    setSelectedRates,
    quantity,
    selectedRates,
    disable_plus,
    disable_minus,
    maxRemainingTickets,
    waiting_list_cta,
    soldout_label,
    goToWaitingList,
    unavailableRate,
    mode,
}) {
    const { t } = useTranslation();

    const onChangeTicketsNumber = React.useCallback((e, value, isDecrement = false) => {
        let _value = parseInt(value) || 0;

        if (isDecrement) {
            if (_value < rate.cart_min_quantity) {
                _value = 0;
            }
        } else {
            if (_value > 0 && _value < rate.cart_min_quantity) {
                _value = rate.cart_min_quantity;
            }
        }

        setQuantity(_value);

        setSelectedRates({
            ...selectedRates,
            [rate.id]: {
                quantity: _value,
                price: rate.price * _value,
            },
        });
    }, [rate, selectedRates]);

    return (
        <div className='neo-rate-right-container'>
            {rate.display_waiting_list && !unavailableRate && (
                <Button
                    className='neo-waiting-list-cta'
                    type='primary'
                    label={waiting_list_cta}
                    onClick={goToWaitingList}
                />
            )}

            {Boolean(mode !== 'sale' && !rate.display_waiting_list && !rate.quantity_available) && (
                <div className='neo-unavailable-rate'>{soldout_label}</div>
            )}

            {Boolean(!unavailableRate && !rate.display_waiting_list) && (
                <Counter
                    value={quantity}
                    onChange={onChangeTicketsNumber}
                    disabledPlus={disable_plus}
                    disabledMinus={disable_minus}
                    max={mode === 'sale' ? maxRemainingTickets : rate.quantity_available}
                    ariaLabelPlus={t('widget.counter.button-plus', { rate: rate.name })}
                    ariaLabelMinus={t('widget.counter.button-minus', { rate: rate.name })}
                    ariaLabelInput={t('widget.counter.input', { rate: rate.name })}
                    ariaLabelAnnouncer={t('widget.counter.selected_quantity', { count: quantity, rate: rate.name })}
                />
            )}

            {unavailableRate && (
                <div className='neo-unavailable-rate'>{t('widget.unavailable')}</div>
            )}
        </div>
    );
}

export default function RateCard({ mode = 'sale', setSelectedRates, selectedRates, rate, max_ticket, disable_plus, disable_minus, currency, event, onSubmit, waiting_list_cta, soldout_label }) {
    const { t } = useTranslation();
    const { name, description } = rate;

    const maxRemainingTickets = React.useMemo(() => {
        if (!max_ticket) {
            return undefined;
        }
        const totalQuantityWithoutCurrentRate = Object.entries(selectedRates).reduce((acc, [key, value]) => {
            if (key !== String(rate.id)) {
                acc += value.quantity;
            }
            return acc;
        }, 0)
        const maxRemainingTotalCard = max_ticket - totalQuantityWithoutCurrentRate;
        return (rate.cart_max_quantity && maxRemainingTotalCard >= rate.cart_max_quantity) ? rate.cart_max_quantity : maxRemainingTotalCard;
    }, [max_ticket, rate, selectedRates]);

    const unavailableRate = React.useMemo(() => {
        return !rate.display_waiting_list && ((mode === 'resale' && !rate.quantity_available) || (!rate.disable && (new Date(rate.visibility_start_date) > new Date())));
    }, [rate, mode]);

    const [showMore, setShowMore] = React.useState(unavailableRate ? false : true);
    const [quantity, setQuantity] = React.useState(rate.preselected_quantity);
    const toggleShowMore = React.useCallback(() => setShowMore(showMore => !showMore), []);

    const hasDescription = React.useMemo(() => {
        return Boolean(rate.description);
    }, [rate]);

    const goToWaitingList = React.useCallback(() => {
        onSubmit({ data: { action: 'waiting_list_ticket_step' } });
    }, [onSubmit]);

    return (
        <div className={`neo-rate-card-content ${!unavailableRate ? 'neo-rate-card-content-active' : ''}`}>
            <div className={`neo-rate-content ${(rate.visibility_start_date) ? 'neo-rate-content-with-date' : ''}`}>
                <div className='neo-rate-left-container'>
                    <div className='neo-rate-title-and-group'>
                        {rate.group_name && (
                            <p className='neo-rate-group'>{rate.group_name}</p>
                        )}
                        <p className='neo-rate-title'>{name}</p>
                        {rate.price > 0 && <p className={`neo-rate-price ${unavailableRate ? 'neo-rate-price-unavailable' : ''}`}>
                            {(rate.min_price !== undefined && rate.max_price > 0)
                                ? `${t('widget.resale.event.from')} ${formattedAmount(rate.min_price, currency)}`
                                : formattedAmount(rate.price, currency)}
                        </p>}
                    </div>
                    <div className='neo-rate-card'>
                        <div className='neo-rate-infos'>
                            {event?.settings?.aff_quantite && rate.quantity_available !== undefined && (
                                <div className='neo-rate-quota'>
                                    {t('widget.resale.tickets.available-quantity')} {rate.quantity_available}
                                </div>
                            )}
                            {
                                rate.visibility_start_date && !unavailableRate && (
                                    <div className='neo-rate-timer-and-date'>
                                        <Icon name='timer' size='small' className='neo-rate-timer-icon' />
                                        <div>
                                            {t('widget.availability')}
                                            {moment(rate.visibility_start_date).format('DD/MM/YYYY HH:mm')}
                                        </div>
                                    </div>
                                )
                            }
                            { hasDescription && (
                                <div className='neo-rate-read-more'>
                                    <div className='neo-read-more-container'>
                                        <div className={`neo-read-more-icon ${unavailableRate ? 'unavailable' : ''}`} onClick={toggleShowMore}>
                                            <Icon name='infoCircle' className={`neo-rate-info-icon ${showMore ? 'active' : ''}`} />
                                            <div className={`neo-rate-read-more ${showMore ? 'active' : ''}`}>{showMore ? t('widget.resale.event.show-less') : t('widget.resale.event.show-more')}</div>
                                        </div>
                                    </div>
                                </div>)
                            }
                        </div>
                    </div>
                </div>
                <RateCounter
                    rate={rate}
                    setQuantity={setQuantity}
                    setSelectedRates={setSelectedRates}
                    quantity={quantity}
                    selectedRates={selectedRates}
                    disable_plus={disable_plus}
                    disable_minus={disable_minus}
                    maxRemainingTickets={maxRemainingTickets}
                    waiting_list_cta={waiting_list_cta}
                    soldout_label={soldout_label}
                    goToWaitingList={goToWaitingList}
                    unavailableRate={unavailableRate}
                    mode={mode}
                />
            </div>
            {hasDescription && (
                <div className={showMore && 'neo-rate-description'}>
                    {showMore && parsedHTML(description)}
                </div>
            )}
        </div>
    )
}
